import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { Camera, Trophy, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty: 'Medium',
    questions: []
  });
  const [csvFile, setCsvFile] = useState(null);
  const [csvError, setCsvError] = useState('');
  const [importing, setImporting] = useState(false);

  // Generate timestamp-based title
  const generateTitle = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${month}${day}${year}-${hours}${minutes}`;
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        console.error('Failed to fetch templates');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setLoading(false);
    }
  };

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        title: template.title,
        description: template.description,
        difficulty: template.difficulty || 'Medium',
        questions: template.questions || []
      });
    } else {
      setEditingTemplate(null);
      setFormData({ 
        title: generateTitle(), 
        description: '', 
        difficulty: 'Medium',
        questions: [] 
      });
    }
    setCsvFile(null);
    setCsvError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setFormData({ title: '', description: '', questions: [] });
    setCsvFile(null);
    setCsvError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      setCsvError('Please select a CSV file');
      setCsvFile(null);
      return;
    }
    
    setCsvFile(file);
    setCsvError('');
    
    // Parse CSV
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setCsvError('Error parsing CSV file');
          console.error('CSV errors:', results.errors);
          return;
        }
        
        // Validate and format questions
        const questions = results.data.map((row, idx) => {
          const questionNum = parseInt(row.Number || row.number || (idx + 1));
          return {
            number: questionNum,
            category: row.Category || row.category || '',
            text: row.Question || row.question || '',
            answer: row.Answer || row.answer || '',
            type: row.Type || row.type || 'regular',
            imageUrl: row['Image URL'] || row.imageUrl || row.image_url || null
          };
        });
        
        // Validate we have questions
        if (questions.length === 0) {
          setCsvError('No valid questions found in CSV');
          return;
        }
        
        // Check required fields
        const missingFields = questions.some(q => !q.text || !q.answer);
        if (missingFields) {
          setCsvError('Some questions are missing required fields (Question, Answer)');
          return;
        }
        
        setFormData({ ...formData, questions });
        setCsvError('');
      },
      error: (error) => {
        setCsvError('Failed to parse CSV: ' + error.message);
        console.error('Parse error:', error);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.questions.length === 0) {
      alert('Please upload a CSV file with questions');
      return;
    }
    
    setImporting(true);
    
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          difficulty: formData.difficulty,
          questions: formData.questions,
          isOfficial: true
        })
      });
      
      if (response.ok) {
        alert('Template created successfully!');
        handleCloseModal();
        fetchTemplates(); // Refresh list
      } else {
        const error = await response.json();
        alert('Failed to create template: ' + (error.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Failed to create template');
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        alert('Template deleted successfully');
        fetchTemplates();
      } else {
        alert('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };
  
  
  const handleSaveQuestions = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/admin/templates/${editingTemplate.id}/questions`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('quizzler_admin_token')}`
      },
      body: JSON.stringify({ questions: editingTemplate.questions })
    });
    
    if (!response.ok) throw new Error('Failed to save questions');
    
    alert('Questions saved successfully!');
    fetchTemplates(); // Refresh the list
  } catch (error) {
    console.error('Error saving questions:', error);
    alert('Failed to save questions');
  }
};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Game Templates</h1>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary"
        >
          + Create Template
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-slate-50 border-l-4 border-slate-500 p-4 mb-6 rounded">
        <p className="text-sm text-gray-700">
          <strong>💡 Tip:</strong> Templates created here are available to all hosts. Upload a CSV file with your trivia questions to create a new template.
        </p>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            No templates yet. Create your first template!
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
              <div className="flex gap-2 mb-3">
                {template.is_official && (
                  <div className="inline-block bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs font-semibold">
                    ⭐ Official
                  </div>
                )}
                {template.difficulty && (
                  <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    template.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                    template.difficulty === 'Hard' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {template.difficulty}
                  </div>
                )}
              </div>

              <h3 className="text-xl font-bold text-gray-800 mb-2">{template.title}</h3>
              <p className="text-gray-600 text-sm mb-4">{template.description}</p>
              
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <span>{template.question_count || 0} questions</span>
                <span>Used {template.times_used || 0}x</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleOpenModal(template)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-semibold transition"
                >
                  View
                </button>
                <button 
                  onClick={() => handleDelete(template.id)}
                  className="flex-1 bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg font-semibold transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {editingTemplate ? 'View Template' : 'Create New Template'}
            </h2>

            {editingTemplate ? (
              // View mode - show template details
              <div>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Title
                  </label>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    {editingTemplate.title}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Description
                  </label>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    {editingTemplate.description || 'No description'}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Difficulty
                  </label>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                      editingTemplate.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                      editingTemplate.difficulty === 'Hard' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {editingTemplate.difficulty || 'Medium'}
                    </span>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Questions ({editingTemplate.question_count || 0})
                  </label>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    {editingTemplate.questions && editingTemplate.questions.length > 0 ? (
  <div className="space-y-3">
    {editingTemplate.questions.map((q, idx) => (
  <div key={idx} className={`border rounded p-3 ${q.type === 'visual' ? 'bg-orange-50 border-orange-200' : q.type === 'final' ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
    <div className="flex justify-between items-center mb-2">
      <span className="font-semibold text-gray-700 flex items-center gap-2">
        {q.type === 'visual' && <Camera size={16} className="text-orange-600" />}
        {q.type === 'final' && <Trophy size={16} className="text-red-600" />}
        {q.type === 'visual' ? 'Visual Round' : q.type === 'final' ? 'Final Question' : `Q${idx + 1}`}
      </span>
      <span className="text-xs text-gray-500">{q.category}</span>
    </div>
    <textarea
      className="w-full border rounded px-3 py-2 mb-2 min-h-[60px] resize-y text-sm"
      value={q.text}
      onChange={(e) => {
        const updated = { ...editingTemplate };
        updated.questions[idx].text = e.target.value;
        setEditingTemplate(updated);
      }}
    />
    <input
      type="text"
      className="w-full border rounded px-3 py-2 text-sm"
      placeholder="Answer"
      value={q.answer}
      onChange={(e) => {
        const updated = { ...editingTemplate };
        updated.questions[idx].answer = e.target.value;
        setEditingTemplate(updated);
      }}
    />
    {q.type === 'visual' && (
      <div className="mt-2">
        <label className="text-xs text-orange-700 font-semibold">Visual Round Image URL:</label>
        <input
          type="text"
          className="w-full border border-orange-300 rounded px-3 py-2 text-sm mt-1"
          placeholder="https://quizzler.pro/img/visual-MMDDYY.jpg"
          value={q.image_url || q.imageUrl || ''}
          onChange={(e) => {
            const updated = { ...editingTemplate };
            updated.questions[idx].image_url = e.target.value;
            setEditingTemplate(updated);
          }}
        />
      </div>
    )}
  </div>
))}
  </div>
) : (
  <p className="text-gray-500">No questions loaded</p>
)}
                  </div>
                </div>

                <div className="flex gap-3">
  <button
    type="button"
    onClick={handleCloseModal}
    className="flex-1 btn-secondary"
  >
    Close
  </button>
  <button
    type="button"
    onClick={handleSaveQuestions}
    className="flex-1 bg-teal-600 text-white py-2 px-4 rounded-lg hover:bg-teal-700 transition"
  >
    Save Changes
  </button>
</div>
              </div>
            ) : (
              // Create mode - show form
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Template Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 bg-gray-50"
                    placeholder="Auto-generated based on date/time"
                    required
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-generated format: MMDDYYYY-HHMM</p>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Difficulty Level *
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                    required
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                    placeholder="A description of this trivia game..."
                    rows="3"
                  />
                </div>

               <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Questions CSV File * 
                    <a 
                      href="https://quizzlertrivia.com/template/quizzler_questions_template.csv" 
                      download
                      className="ml-2 text-blue-600 hover:text-blue-800 font-normal text-xs"
                    >
                      (Download CSV Template)
                    </a>
                  </label>
                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-semibold
                        file:bg-slate-100 file:text-slate-700
                        hover:file:bg-slate-200
                        cursor-pointer"
                    />
                    {csvFile && !csvError && (
                      <div className="mt-3 text-sm text-green-600">
                        <CheckCircle size={16} className="inline mr-1 text-green-600" /> {csvFile.name} - {formData.questions.length} questions loaded
                      </div>
                    )}
                    {csvError && (
                      <div className="mt-3 text-sm text-red-600">
                        <XCircle size={16} className="inline mr-1 text-red-600" /> {csvError}
                      </div>
                    )}
                    <div className="mt-3 text-xs text-gray-500">
                      <strong>CSV Format:</strong> Number, Category, Question, Answer, Type, Image URL
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 btn-secondary"
                    disabled={importing}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-primary"
                    disabled={importing || formData.questions.length === 0}
                  >
                    {importing ? 'Creating...' : 'Create Template'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}